import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';

export default function ShowroomSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const o = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) ref.current?.classList.add('visible');
    }, { threshold: 0.1 });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, []);

  return (
    <section className="py-28 bg-cream-DEFAULT overflow-hidden" itemScope itemType="https://schema.org/FurnitureStore">
      <div ref={ref} className="fade-up max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-8 h-px bg-gold" />
            <span className="text-gold text-xs font-bold uppercase tracking-[0.25em]">Visit Us In Person</span>
            <span className="w-8 h-px bg-gold" />
          </div>
          <h2 className="font-serif text-5xl md:text-6xl font-bold text-dark leading-tight mb-4">
            Experience Our
            <br />
            <em className="text-gold not-italic">Las Vegas Showroom</em>
          </h2>
          <p className="text-dark/50 text-base max-w-xl mx-auto leading-relaxed">
            Our 10,000 sq ft showroom features 500+ pieces on display. Come see, touch, and experience the quality 
            of every piece — with a free design consultation included.
          </p>
        </div>

        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-cream-dark">
          {/* Map — large and visually dominant */}
          <div className="relative h-[480px]">
            <iframe
              title="Eclectic Home Showroom Location Las Vegas"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3223.3!2d-115.1728!3d36.1060!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80c8c43d3ec02c43%3A0x0!2s3750+S+Las+Vegas+Blvd%2C+Las+Vegas%2C+NV+89109!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            {/* Map overlay badge */}
            <div className="absolute top-5 left-5 bg-white rounded-xl px-4 py-3 flex items-center gap-3" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gold/10">
                <i className="ri-map-pin-2-fill text-gold" />
              </div>
              <div>
                <div className="text-dark font-bold text-xs uppercase tracking-wider">Eclectic Home</div>
                <div className="text-dark/50 text-[11px]">Las Vegas, NV 89109</div>
              </div>
            </div>
          </div>

          {/* Info panel */}
          <div className="bg-dark p-12 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3 mb-8">
                <span className="w-6 h-px bg-gold" />
                <span className="text-gold text-[10px] font-bold uppercase tracking-[0.25em]">Showroom Info</span>
              </div>

              <h3 className="font-serif text-3xl font-bold text-white mb-2" itemProp="name">Eclectic Home Furniture</h3>
              <p className="text-white/40 text-sm mb-8 leading-relaxed" itemProp="description">
                Las Vegas&apos; most curated furniture showroom — featuring premium pieces for every room in your home.
              </p>

              {/* Contact details */}
              <div className="space-y-5 mb-10">
                <a href="tel:7028509900" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/10 group-hover:bg-gold/20 transition-colors flex-shrink-0">
                    <i className="ri-phone-fill text-gold" />
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-wider">Phone</div>
                    <div className="text-white font-semibold text-sm group-hover:text-gold transition-colors" itemProp="telephone">(702) 850-9900</div>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/10 flex-shrink-0">
                    <i className="ri-map-pin-line text-gold" />
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-wider">Address</div>
                    <div className="text-white font-semibold text-sm" itemProp="address">3750 S Las Vegas Blvd, Las Vegas, NV 89109</div>
                  </div>
                </div>

                <a href="mailto:hello@eclectichome.com" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/10 group-hover:bg-gold/20 transition-colors flex-shrink-0">
                    <i className="ri-mail-line text-gold" />
                  </div>
                  <div>
                    <div className="text-white/40 text-[10px] uppercase tracking-wider">Email</div>
                    <div className="text-white font-semibold text-sm group-hover:text-gold transition-colors" itemProp="email">hello@eclectichome.com</div>
                  </div>
                </a>
              </div>

              {/* Hours */}
              <div className="border-t border-white/10 pt-7">
                <div className="text-white/40 text-[10px] uppercase tracking-[0.2em] mb-4">Showroom Hours</div>
                <div className="space-y-2">
                  {[
                    { days: 'Monday – Friday', hours: '10:00 AM – 7:00 PM' },
                    { days: 'Saturday', hours: '10:00 AM – 6:00 PM' },
                    { days: 'Sunday', hours: '11:00 AM – 5:00 PM' },
                  ].map((h) => (
                    <div key={h.days} className="flex justify-between text-sm">
                      <span className="text-white/50">{h.days}</span>
                      <span className="text-white font-medium">{h.hours}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-3 mt-8">
              <Link
                to="/showroom"
                className="text-center bg-gold text-white py-4 rounded-lg text-xs font-bold uppercase tracking-widest hover:bg-gold-dark transition-colors cursor-pointer whitespace-nowrap"
              >
                Book a Free Design Consultation
              </Link>
              <a
                href="tel:7028509900"
                className="text-center border border-white/20 text-white py-4 rounded-lg text-xs font-semibold uppercase tracking-widest hover:border-white/40 transition-colors cursor-pointer whitespace-nowrap"
              >
                Call (702) 850-9900
              </a>
            </div>
          </div>
        </div>

        {/* Service area chips */}
        <div className="mt-10 flex flex-wrap items-center gap-3">
          <span className="text-dark/40 text-xs font-semibold uppercase tracking-[0.18em]">Serving:</span>
          {['Las Vegas', 'Henderson', 'Summerlin', 'North Las Vegas', 'Paradise', 'Spring Valley', 'Enterprise', 'Boulder City'].map((city) => (
            <span key={city} className="bg-cream-mid border border-cream-dark text-dark/60 px-4 py-1.5 rounded-full text-xs font-medium hover:border-gold hover:text-gold transition-colors cursor-default">
              {city}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
